import {
  ThemeOptions,
  createTheme,
  responsiveFontSizes,
} from '@mui/material/styles';

const statusError = '#C50000';
const statusErrorOld = '#910c01';
const statusWarning = '#F2C94C';
const statusOk = '#5AB652';
const statusInfo = '#1677CE';

const white = '#FFFFFF';
const paper = '#D6D8DC';
const cardboard = '#858B91';
const black = '#000000';
const background = '#E5E5E5';
const trimMain = '#333E48';

export const primaryMain = '#ED7300';
const secondaryMain = '#1677CE';
const tertiaryMain = '#4C00ED';

const textPrimary = '#333E48';
const textSecondary = '#4F4F4F';
const textDisabled = '#bababb';

export const orange40 = '#F8C799';

declare module '@mui/material/styles' {
  interface TypeBackground {
    cardboard: string;
  }

  interface Palette {
    trim: Palette['primary'];
    tertiary: Palette['primary'];
    primaryDisabled: Palette['primary'];
  }
  interface PaletteOptions {
    trim: PaletteOptions['primary'];
    tertiary: PaletteOptions['primary'];
    primaryDisabled: PaletteOptions['primary'];
  }
}

const themeOptions: ThemeOptions = {
  palette: {
    common: {
      white,
      black,
    },
    primary: { main: primaryMain },
    secondary: { main: secondaryMain },
    tertiary: { main: tertiaryMain },
    background: {
      default: background,
      paper,
      cardboard,
    },
    text: {
      primary: textPrimary,
      secondary: textSecondary,
      disabled: textDisabled,
    },
    error: { main: statusError },
    warning: { main: statusWarning },
    info: { main: statusInfo },
    success: { main: statusOk },
    trim: { main: trimMain },
    primaryDisabled: { main: orange40 },
  },
  typography: { fontFamily: 'Interstate, sans-serif' },
  components: {
    MuiCard: {
      defaultProps: {
        sx: {
          borderRadius: 3,
          backgroundColor: white,
          borderColor: primaryMain,
        },
      },
    },
    MuiButton: {
      defaultProps: {
        sx: {
          borderRadius: '20px',
          textTransform: 'none',
          boxShadow: 'none',
          backgroundColor: 'primary',
          color: white,
          fontWeight: 'bold',
        },
      },
    },
    MuiInputLabel: {
      defaultProps: {
        sx: {
          fontWeight: '700',
          fontSize: '12px',
          lineHeight: '16px',
          padding: '0 0 0 8px',
          margin: '0 0 4px 0',
          color: textPrimary,
        },
      },
    },
    MuiFormControlLabel: {
      defaultProps: {
        sx: {
          fontWeight: '700',
          fontSize: '12px',
          lineHeight: '16px',
          padding: '0 0 0 0',
          margin: '0 0 4px 0',
          color: textPrimary,
        },
      },
    },
    MuiFormLabel: {
      defaultProps: {
        sx: {
          fontWeight: '700',
          fontSize: '12px',
          lineHeight: '16px',
          padding: '0 0 0 0',
          margin: '0 0 4px 0',
          color: textPrimary,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          padding: '12px 16px !important',
          '&:before': { color: '#ED7300' },
          '&:after': { color: '#ED7300' },
        },
        icon: { fill: '#ED7300' },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          backgroundColor: '#fff',
          '&.Mui-selected': {
            borderColor: '#fff',
            backgroundColor: '#fff',
            fontWeight: '600',
          },
          '&.Mui-disabled': { fontWeight: '400' },
          '&.Mui-focusVisible': { backgroundColor: '#fff' },
          '&.Mui-focusVisible:hover': { backgroundColor: '#EFF0F1 !important' },
          '&.Mui-selected:hover': { backgroundColor: '#EFF0F1 !important' },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .Mui-disabled': { opacity: 1, '-webkit-text-fill-color': '#5D6771 !important' },
          '&.Mui-disabled fieldset': { borderColor: '#BEC2C6 !important' },
          '& fieldset': { borderColor: '#70787F !important' },
          '&.Mui-error fieldset': { borderColor: `${statusError} !important` },
          '&.Mui-focused fieldset': { borderColor: '#70787F !important', borderWidth: '1px !important' },
        },
        input: {
          backgroundColor: white,
          borderColor: '#70787F',
          '&::placeholder': { opacity: 1, color: '#8E959C' },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          paddingLeft: '8px',
          '&.Mui-error': { color: `${statusError} !important` },
        },
      },
    },
    MuiSvgIcon: { styleOverrides: { root: { color: '#ED7300' } } },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: white,
          '& .MuiCalendarOrClockPicker-root': { width: '256px', maxHeight: '290px' },
          '& .MuiCalendarPicker-root': { width: '256px', maxHeight: '290px' },
          '& .MuiPickersCalendarHeader-root': { paddingLeft: '16px', paddingRight: '4px', marginTop: '8px' },
          '& .MuiDayPicker-header': { borderTop: '1px solid #9EA4AA99', borderBottom: '1px solid #9EA4AA99' },
          '& .MuiDayPicker-weekDayLabel': {
            width: '32px',
            height: '32px',
          },
          '& .MuiPickersDay-root': {
            backgroundColor: white,
            width: '32px',
            height: '32px',
          },
          '& .Mui-selected': {
            backgroundColor: `${white} !important`,
            border: `1px solid ${primaryMain}`,

            '&:hover': { backgroundColor: `${white} !important` },
            '&:focus': { backgroundColor: `${white} !important` },
          },
          '& .MuiYearPicker-root': { justifyContent: 'center' },
        },
      },
    },
  },
};

const themeRequestQuoteOptions: ThemeOptions = {
  palette: {
    error: { main: statusErrorOld },
    trim: { main: trimMain },
    tertiary: { main: tertiaryMain },
    primaryDisabled: { main: orange40 },
  },
  typography: { fontFamily: 'Interstate, sans-serif' },
  components: {
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: `${white} !important`,
          letterSpacing: '0.5px',
          marginBottom: '2px !important',
          fontWeight: '600 !important',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&.Mui-error': { borderColor: `${statusErrorOld} !important` },
          '&.Mui-error fieldset': { borderColor: `${statusErrorOld} !important` },
        },
        input: {
          borderRadius: '4px',
          padding: '12px 16px',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: `${statusErrorOld} !important`,
          '&.Mui-error': { color: `${statusErrorOld} !important` },
        },
      },
    },
  },
};

export const themeRequestQuote = responsiveFontSizes(createTheme(themeOptions, themeRequestQuoteOptions));

export default responsiveFontSizes(createTheme(themeOptions));
